/**
 * B2B Forms Service
 * Handles B2B join network form submissions to Firestore
 */
import { addDocument, getDocument, updateDocument, removeDocument } from '../firebase/firestore.js'
import { getFirebaseDb } from '../firebase/firebase.js'
import { collection, query, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore'

const COLLECTION_NAME = 'b2bForms'

/**
 * Get user's IP address from external service
 * @returns {Promise<string|null>}
 */
async function getClientIp() {
  try {
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    return data.ip || null
  } catch {
    return null
  }
}

/**
 * Submit a B2B join network form
 * @param {Object} formData - Form data from JoinNetworkView
 * @param {string} formData.city - Selected city
 * @param {string} formData.biz_name - Business name
 * @param {string} formData.vat - VAT / Business ID number
 * @param {string} formData.address - Full address
 * @param {string} formData.contact - Contact person name
 * @param {string} formData.phone - Phone number
 * @param {string} formData.email - Email address
 * @param {string} formData.supply - Supply schedule preferences
 * @param {boolean} formData.tos - Terms of service acceptance
 * @param {boolean} formData.marketing - Marketing consent
 * @returns {Promise<{success: boolean, docId: string}>}
 */
export async function submitB2BForm(formData) {
  const ipAddress = await getClientIp()
  const now = new Date()

  const b2bFormDoc = {
    // Form fields
    city: formData.city,
    biz_name: formData.biz_name?.trim() || '',
    vat: formData.vat?.trim() || '',
    address: formData.address?.trim() || '',
    contact: formData.contact?.trim() || '',
    phone: formData.phone?.trim() || '',
    email: formData.email?.trim() || '',
    supply: formData.supply?.trim() || '',
    tos: formData.tos || false,
    marketing: formData.marketing || false,

    // Meta fields
    status: 'New',
    createdAt: serverTimestamp(),
    submittedDate: now.toLocaleDateString('he-IL'),
    submittedTime: now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }),
    ipAddress,
    userAgent: navigator.userAgent || null,
  }

  const docId = await addDocument(COLLECTION_NAME, b2bFormDoc)

  return { success: true, docId }
}

/**
 * Subscribe to B2B forms collection (realtime)
 * @param {Function} callback - (forms, error) => void
 * @returns {Function} Unsubscribe function
 */
export function subscribeB2BForms(callback) {
  const db = getFirebaseDb()
  const q = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'))

  return onSnapshot(
    q,
    (snapshot) => {
      const forms = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      callback(forms, null)
    },
    (error) => {
      callback(null, error)
    }
  )
}

/**
 * Get a single B2B form by ID
 * @param {string} formId - Form document ID
 * @returns {Promise<Object|null>}
 */
export async function getB2BFormById(formId) {
  const doc = await getDocument(COLLECTION_NAME, formId)
  if (doc) {
    return { id: formId, ...doc }
  }
  return null
}

/**
 * Delete a B2B form by ID
 * @param {string} formId - Form document ID
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteB2BForm(formId) {
  await removeDocument(COLLECTION_NAME, formId)
  return { success: true }
}

/**
 * Add a new note to a B2B form
 * @param {string} formId - Form document ID
 * @param {string} content - Note content
 * @param {Object} author - Author info { uid, name }
 * @returns {Promise<{success: boolean, note: Object}>}
 */
export async function addB2BFormNote(formId, content, author) {
  const now = new Date().toISOString()
  const noteId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  const newNote = {
    id: noteId,
    content: content?.trim() || '',
    createdAt: now,
    createdBy: {
      uid: author.uid,
      name: author.name,
    },
  }

  // Get current form to append to notes array
  const form = await getDocument(COLLECTION_NAME, formId)
  const currentNotes = form?.notes || []

  await updateDocument(COLLECTION_NAME, formId, {
    notes: [...currentNotes, newNote],
  })

  return { success: true, note: newNote }
}

/**
 * Update an existing note in a B2B form
 * @param {string} formId - Form document ID
 * @param {string} noteId - Note ID to update
 * @param {string} content - New note content
 * @param {Object} editor - Editor info { uid, name }
 * @returns {Promise<{success: boolean}>}
 */
export async function updateB2BFormNote(formId, noteId, content, editor) {
  const now = new Date().toISOString()

  // Get current form
  const form = await getDocument(COLLECTION_NAME, formId)
  const currentNotes = form?.notes || []

  // Find and update the note
  const updatedNotes = currentNotes.map((note) => {
    if (note.id === noteId) {
      return {
        ...note,
        content: content?.trim() || '',
        updatedAt: now,
        updatedBy: {
          uid: editor.uid,
          name: editor.name,
        },
      }
    }
    return note
  })

  await updateDocument(COLLECTION_NAME, formId, {
    notes: updatedNotes,
  })

  return { success: true }
}

/**
 * Status labels mapping (English key to Hebrew display)
 */
export const STATUS_LABELS = {
  New: 'חדש',
  Received: 'התקבל',
  InOpening: 'בפתיחה',
  Rejected: 'סורב',
  Application: 'אפליקציה',
  Agent: 'סוכן',
  Ordered: 'הזמנה',
  NotOrdered: 'לא הזמין',
  NotInterested: 'לא מעוניין',
}

/**
 * Update the status of a B2B form and add to status history
 * @param {string} formId - Form document ID
 * @param {string} newStatus - New status value
 * @param {Object} user - User info { uid, name }
 * @returns {Promise<{success: boolean, statusEntry: Object}>}
 */
export async function updateB2BFormStatus(formId, newStatus, user, clientData = null) {
  const now = new Date().toISOString()
  const entryId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  // Get current form
  const form = await getDocument(COLLECTION_NAME, formId)
  const currentStatus = form?.status || 'New'
  const currentHistory = form?.statusHistory || []

  // Create status history entry
  const statusEntry = {
    id: entryId,
    fromStatus: currentStatus,
    toStatus: newStatus,
    changedBy: {
      uid: user.uid,
      name: user.name,
    },
    changedAt: now,
  }

  // Build update payload
  const updatePayload = {
    status: newStatus,
    statusChangedAt: now,
    statusHistory: [...currentHistory, statusEntry],
  }

  // Save fetched client data from Priority if provided
  if (clientData) {
    updatePayload.client = clientData
  }

  // Update document with new status and history
  await updateDocument(COLLECTION_NAME, formId, updatePayload)

  return { success: true, statusEntry }
}

/**
 * Reset a B2B form to its original state (before any processing)
 * @param {string} formId - Form document ID
 * @returns {Promise<{success: boolean}>}
 */
export async function resetB2BForm(formId) {
  await updateDocument(COLLECTION_NAME, formId, {
    status: 'New',
    statusChangedAt: null,
    statusHistory: [],
    notes: [],
  })

  return { success: true }
}

/**
 * Update application steps for a B2B form
 * @param {string} formId - Form document ID
 * @param {string} stepKey - Step key ('contacted' or 'validated_technical')
 * @param {Object} user - User who completed the step {uid, name}
 * @returns {Promise<{success: boolean}>}
 */
export async function updateApplicationStep(formId, stepKey, user) {
  const now = new Date().toISOString()

  // Get current form
  const form = await getDocument(COLLECTION_NAME, formId)
  const currentSteps = form?.application_steps || {}

  // Build updated steps object
  const updatedSteps = {
    ...currentSteps,
    [stepKey]: {
      completed: true,
      completedAt: now,
      completedBy: {
        uid: user.uid,
        name: user.name,
      },
    },
  }

  // Update document
  await updateDocument(COLLECTION_NAME, formId, {
    application_steps: updatedSteps,
  })

  return { success: true, steps: updatedSteps }
}
